// src/admin/sections/AuditLogSection.jsx
//
// Ticket 62 -- Admin: Audit log. A read-only list of admin_audit_log, newest
// first: who did it, what, and the reason where there is one.
//
// WHAT IS ON A LINE
//   who     `actor`, stamped by the database from the caller's sign-in
//           (trg_stamp_audit_actor), never typed by the screen.
//   what    `action_type` (create / update / delete / merge) and `entity_type`,
//           then `description`, shown whole.
//   reason  there is no reason column. A reason is written INTO `description`
//           by the action that took it ("Rejected X -- <reason>",
//           "Suspended @x -- "<reason>""). The description is shown exactly as
//           written and is never split on a dash: a book title can hold one,
//           and a guessed split would put half a title in the wrong box.
//
// WHAT THE LOG HOLDS
//   An entry is written only after the write it describes succeeded
//   (docs/admin-panel-spec.md rule 6), so this list is what happened, not what
//   was attempted. It does not hold reads, and it does not hold actions that
//   have not been given a line yet.
//
// A READ THAT FAILS IS NOT AN EMPTY LOG
//   A request that fails shows the error. A table the signed-in admin is not
//   allowed to read comes back as zero rows with no error at all, which looks
//   exactly like an empty log, so the empty state says so rather than claiming
//   "nothing has happened".
//
// Read only: nothing on this screen writes. scripts/check-audit-log-section.mjs
// fails the build if it ever does.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';

// The four values admin_audit_log_action_type_check allows. The guard reads the
// constraint out of the schema and fails the build if this list drifts from it.
export const ACTION_TYPES = ['create', 'update', 'delete', 'merge'];

export const PAGE_SIZE = 50;

// The ids are uuids or numbers written as text. Long ones are shortened to read
// on a phone; the whole value is in the tooltip.
export function shortId(id) {
  const s = id == null ? '' : String(id).trim();
  if (!s) return '';
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

// 'create' -> 'Create'. The database's own word, not a friendlier one: a
// rejected book is logged as 'delete', and a softer label would hide that.
export function actionLabel(type) {
  return typeof type === 'string' && type ? type[0].toUpperCase() + type.slice(1) : 'Unknown';
}

export function whenText(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown time';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// One page of the log. Returns { ok, rows, total, error }. `from` is how many
// lines are already on screen. A line written while the admin is reading pushes
// older ones down by one, so a page can repeat a line but never skips one; the
// caller drops a repeat by id.
export async function loadAuditPage({ action = 'all', from = 0 } = {}) {
  let q = supabase
    .from('admin_audit_log')
    .select('id, actor, action_type, entity_type, description, entity_id, created_at', { count: 'exact' })
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(from, from + PAGE_SIZE - 1);
  if (action !== 'all') q = q.eq('action_type', action);
  const { data, error, count } = await q;
  if (error) return { ok: false, rows: [], total: null, error: error.message };
  return { ok: true, rows: data || [], total: typeof count === 'number' ? count : null, error: null };
}

// PAGING WHILE LINES ARE BEING WRITTEN
// Lines are only ever added to this table (there is no UPDATE or DELETE policy),
// and new ones go on top. So the number of lines written since the list was read
// is exactly the latest total minus the total of the first read, and the next
// older line is that many places further down than the lines on screen.
// `firstTotal` is the total of the first read of the current filter; `total` is
// the latest one.
export function linesWritten({ firstTotal, total }) {
  return firstTotal == null || total == null ? 0 : Math.max(0, total - firstTotal);
}

// Where the next older line is now.
export function nextFrom({ shown, firstTotal, total }) {
  return shown + linesWritten({ firstTotal, total });
}

// Older lines not yet on screen. Newer ones do not count: they are the note's job.
export function olderLeft({ firstTotal, shown }) {
  return firstTotal == null ? 0 : Math.max(0, firstTotal - shown);
}

// After a page of older lines comes back: the lines to add, and whether to keep
// offering "older". This is the safety net under the arithmetic above: a page
// that came back short, or added no new line, ends paging rather than leaving a
// button that loads nothing.
export function mergePage(existing, fetched) {
  const seen = new Set(existing.map(r => r.id));
  const fresh = fetched.filter(r => !seen.has(r.id));
  return {
    rows: fresh.length ? [...existing, ...fresh] : existing,
    exhausted: fetched.length < PAGE_SIZE || fresh.length === 0,
  };
}

// Is there something an "older" button could still load?
export function canOfferMore({ error, exhausted, firstTotal, shown }) {
  return !error && !exhausted && olderLeft({ firstTotal, shown }) > 0;
}

// What the screen holds after the first read of a filter.
export function firstView(r) {
  return { rows: r.rows, firstTotal: r.total, total: r.total, exhausted: r.rows.length < PAGE_SIZE };
}

// The next page of older lines. If more lines were written since the totals were
// last read (a burst between two clicks), the answer's own total says so: ask
// again from the corrected place, so a burst never makes the page all repeats.
export async function olderView(view, action) {
  const shown = view.rows.length;
  let from = nextFrom({ shown, firstTotal: view.firstTotal, total: view.total });
  let r;
  for (let attempt = 0; attempt < 3; attempt++) {
    r = await loadAuditPage({ action, from });
    if (!r.ok || r.total == null) break;
    const want = nextFrom({ shown, firstTotal: view.firstTotal, total: r.total });
    if (want === from) break;
    from = want;
  }
  if (!r.ok) return { ok: false, error: r.error, view };
  const merged = mergePage(view.rows, r.rows);
  return { ok: true, error: null, view: { rows: merged.rows, firstTotal: view.firstTotal, total: r.total ?? view.total, exhausted: merged.exhausted } };
}

const pillStyle = {
  fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
  padding: '2px 6px', borderRadius: 'var(--radius-md)', border: '1px solid var(--neutral-200)',
  background: 'var(--neutral-50)', color: 'var(--neutral-700)', whiteSpace: 'nowrap',
};

function Line({ row, isMobile }) {
  return (
    <li data-audit-line={row.id} style={{ listStyle: 'none', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '10px 12px' : '12px 16px', marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <span data-action={row.action_type} style={pillStyle}>{actionLabel(row.action_type)}</span>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>{row.entity_type || 'unknown'}</span>
        {shortId(row.entity_id) && (
          <span title={String(row.entity_id)} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontFamily: 'monospace' }}>{shortId(row.entity_id)}</span>
        )}
        <time dateTime={row.created_at} style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>{whenText(row.created_at)}</time>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)', lineHeight: 1.45, overflowWrap: 'anywhere' }}>{row.description || 'No description was recorded.'}</p>
      <div style={{ marginTop: 6, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
        By <strong style={{ color: 'var(--neutral-700)' }}>{row.actor || 'unknown'}</strong>
      </div>
    </li>
  );
}

export default function AuditLogSection({ isMobile }) {
  const [action, setAction]   = useState('all');
  const [view, setView]     = useState({ rows: [], firstTotal: null, total: null, exhausted: false });
  const [loading, setLoading] = useState(true);
  const [more, setMore]       = useState(false);
  const [error, setError]     = useState(null);
  // A slow answer to an old filter must not overwrite the answer to the new one.
  const ticket = useRef(0);

  const loadFirst = useCallback(async (which) => {
    const mine = ++ticket.current;
    setLoading(true);
    setError(null);
    const r = await loadAuditPage({ action: which, from: 0 });
    if (mine !== ticket.current) return;
    setView(firstView(r));
    setError(r.ok ? null : r.error);
    setLoading(false);
  }, []);

  useEffect(() => { loadFirst(action); }, [action, loadFirst]);

  async function loadMore() {
    const mine = ++ticket.current;
    setMore(true);
    const next = await olderView(view, action);
    if (mine !== ticket.current) return;
    if (next.ok) setView(next.view);
    else setError(next.error);
    setMore(false);
  }

  const { rows, firstTotal, total, exhausted } = view;
  const shown = rows.length;
  const canMore = canOfferMore({ error, exhausted, firstTotal, shown });
  const written = error ? 0 : linesWritten({ firstTotal, total });
  const filterWord = action === 'all' ? '' : ` ${action}`;

  return (
    <div style={{ padding: isMobile ? 16 : '30px 36px', fontFamily: 'var(--font-body)', maxWidth: 860 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 0 }}>Audit log</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 4 }}>
          What admins have done, newest first. Read only. A line is written only after the action went through, so this is what happened, not what was tried. Where an action took a reason, the reason is part of the line.
        </p>
      </div>

      <div role="group" aria-label="Filter by action" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {['all', ...ACTION_TYPES].map(a => (
          <button
            key={a}
            type="button"
            onClick={() => setAction(a)}
            aria-pressed={action === a}
            data-filter={a}
            style={{
              fontFamily: 'inherit', fontSize: 'var(--font-size-sm)', fontWeight: action === a ? 700 : 500, cursor: 'pointer',
              padding: '6px 12px', borderRadius: 'var(--radius-lg)',
              border: `1px solid ${action === a ? 'var(--neutral-700)' : 'var(--neutral-200)'}`,
              background: action === a ? 'var(--neutral-800)' : 'var(--brand-white)',
              color: action === a ? 'var(--brand-white)' : 'var(--neutral-700)',
            }}
          >{a === 'all' ? 'All' : actionLabel(a)}</button>
        ))}
      </div>

      {loading && <p role="status" style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-sm)' }}>Reading the log…</p>}

      {!loading && error && (
        <p role="alert" style={{ color: 'var(--feedback-warning-ink)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 'var(--font-size-sm)' }}>
          The log could not be read: {error}{rows.length ? ` Showing the ${rows.length} lines already loaded.` : ''}
          {' '}<button type="button" onClick={() => loadFirst(action)} style={{ fontFamily: 'inherit', cursor: 'pointer' }}>Try again</button>
        </p>
      )}

      {!loading && !error && rows.length === 0 && (
        <p role="status" data-empty="true" style={{ color: 'var(--neutral-600)', fontSize: 'var(--font-size-sm)', lineHeight: 1.5 }}>
          {action === 'all'
            ? 'No lines to show. If an admin has just taken an action and this is still empty, this account may not be allowed to read the log.'
            : `No${filterWord} lines to show. Try All.`}
        </p>
      )}

      {rows.length > 0 && (
        <>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '0 0 8px' }}>
            {firstTotal != null ? `Showing ${rows.length} of ${firstTotal}${filterWord} ${firstTotal === 1 ? 'line' : 'lines'}` : `Showing ${rows.length}${filterWord} lines`}
          </p>
          <ul aria-label="Audit log lines" style={{ margin: 0, padding: 0 }}>
            {rows.map(r => <Line key={r.id} row={r} isMobile={isMobile} />)}
          </ul>
          {written > 0 && (
            <p role="status" data-newer-lines="true" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', margin: '8px 0 0' }}>
              {written} {written === 1 ? 'line was' : 'lines were'} written while you were reading.
              {' '}<button type="button" onClick={() => loadFirst(action)} style={{ fontFamily: 'inherit', cursor: 'pointer' }}>Refresh</button>
            </p>
          )}
          {canMore && (
            <button type="button" onClick={loadMore} disabled={more} style={{ fontFamily: 'inherit', fontSize: 'var(--font-size-sm)', cursor: more ? 'default' : 'pointer', padding: '8px 14px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--neutral-200)', background: 'var(--brand-white)', color: 'var(--neutral-700)' }}>
              {more ? 'Loading…' : `Show ${Math.min(PAGE_SIZE, olderLeft({ firstTotal, shown }))} older`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
